import { PALETTE } from '../constants/enums';

export interface ColorSwatch {
  hex: string;
  label: string;
}

export const MAX_RECENT_COLORS = 16;

/** Один формат для введённого HEX, сохранённой истории и цветов палитры. */
export function normalizeHexColor(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const hex = `#${value.trim().replace(/^#/, '')}`.toLowerCase();
  return /^#[0-9a-f]{6}$/.test(hex) ? hex : null;
}

/** История хранится от последнего выбора к более раннему. */
export function normalizeRecentColors(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const colors = new Set<string>();
  for (const item of value) {
    const hex = normalizeHexColor(item);
    if (hex) colors.add(hex);
    if (colors.size === MAX_RECENT_COLORS) break;
  }
  return [...colors];
}

export function rememberRecentColor(history: unknown, value: unknown): string[] {
  const colors = normalizeRecentColors(history);
  const hex = normalizeHexColor(value);
  return hex ? normalizeRecentColors([hex, ...colors]) : colors;
}

/** Выбранные цвета идут первыми, подписи стандартных образцов сохраняются. */
export function mergeColorPalette(
  defaults: readonly ColorSwatch[],
  history: unknown,
  current?: string | null,
): ColorSwatch[] {
  const labels = new Map<string, string>();
  for (const color of defaults) {
    const hex = normalizeHexColor(color.hex);
    if (hex && !labels.has(hex)) labels.set(hex, color.label);
  }
  const ordered = new Set(normalizeRecentColors(history));
  const selected = normalizeHexColor(current);
  if (selected && !labels.has(selected)) ordered.add(selected);
  for (const hex of labels.keys()) ordered.add(hex);
  return [...ordered].map((hex) => ({ hex, label: labels.get(hex) ?? hex }));
}

/**
 * Цвета назначаются системой, а не красятся вручную — в этом весь смысл
 * перехода с Google Sheets, где заливка была скрытыми данными (Р-06).
 */

/** Устойчивый цвет по идентификатору: один и тот же id всегда даёт один цвет. */
export function colorFromId(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = (hash << 5) - hash + id.charCodeAt(i);
    hash |= 0;
  }
  const index = Math.abs(hash) % PALETTE.length;
  return PALETTE[index] ?? PALETTE[0]!;
}

/**
 * Контрастный цвет текста для произвольного фона.
 * Нужен, чтобы подписи на цветных статусах и группах оставались читаемыми,
 * какой бы цвет ни выбрал администратор.
 */
const DARK_TEXT = '#111827';
const LIGHT_TEXT = '#ffffff';

/** Относительная яркость по WCAG 2.1. */
function relativeLuminance(hex: string): number | null {
  const c = hex.replace('#', '');
  if (c.length !== 6) return null;
  const toLinear = (v: number) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return (
    0.2126 * toLinear(parseInt(c.slice(0, 2), 16)) +
    0.7152 * toLinear(parseInt(c.slice(2, 4), 16)) +
    0.0722 * toLinear(parseInt(c.slice(4, 6), 16))
  );
}

/** Коэффициент контраста между двумя яркостями. */
function contrastRatio(l1: number, l2: number): number {
  const [hi, lo] = l1 > l2 ? [l1, l2] : [l2, l1];
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * Выбирает цвет текста, дающий бо́льший контраст с фоном.
 *
 * Порог не подбирается на глаз: считаются оба коэффициента — с тёмным
 * текстом и со светлым — и берётся больший. Подобранный «на глаз» порог
 * 0.45 давал белый текст на янтарном фоне с контрастом 2.15:1 при
 * минимуме 4.5:1 по AA, тогда как тёмный текст давал 8.26:1.
 */
export function contrastText(hex: string): string {
  const L = relativeLuminance(hex);
  if (L === null) return DARK_TEXT;

  const withDark = contrastRatio(L, relativeLuminance(DARK_TEXT)!);
  const withLight = contrastRatio(L, relativeLuminance(LIGHT_TEXT)!);

  return withDark >= withLight ? DARK_TEXT : LIGHT_TEXT;
}

/** Коэффициент контраста текста на фоне — для проверок доступности. */
export function textContrastRatio(background: string, text: string): number {
  const bg = relativeLuminance(background);
  const fg = relativeLuminance(text);
  if (bg === null || fg === null) return 1;
  return contrastRatio(bg, fg);
}

/** Полупрозрачная версия цвета — для подсветки строки, чтобы текст читался. */
export function withAlpha(hex: string, alpha: number): string {
  const c = hex.replace('#', '');
  if (c.length !== 6) return hex;
  const r = parseInt(c.slice(0, 2), 16);
  const g = parseInt(c.slice(2, 4), 16);
  const b = parseInt(c.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
